import { Component, ElementRef, EventEmitter, Input, NgModule, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LoadingAttributeComponent, ListLoadingIndicatorComponent } from '@shared/ui';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

export interface MethodTypeListItem {
  id: string;
  name: string;
  usedByCaseTypes?: number;
  usedBySamples?: number;
  assigned?: boolean;
  pending?: boolean;
}

export enum MethodTypeColumn {
  NAME = 'name',
  USED_BY_CASE_TYPES = 'usedByCaseTypes',
  USED_BY_SAMPLES = 'usedBySamples',
  EDIT = 'edit',
  DELETE = 'delete',
  ASSIGNED = 'assigned',
}

@Component({
  selector: 'ts-method-types-list',
  templateUrl: './method-types-list.component.html',
  styleUrls: ['./method-types-list.component.scss'],
  standalone: false,
})
export class MethodTypesListComponent implements OnChanges {
  @Input() items: MethodTypeListItem[] = [];
  @Input() loading = false;
  @Input() noMethodTypes = false;
  @Input() emptyMessageKey = 'info.no_method_types';
  /*
   * Which columns to render. Defaults to the full catalog layout so the existing
   * catalog page keeps working unchanged; the manager passes compact column sets
   * (e.g. [NAME, ASSIGNED] / [NAME, EDIT, ASSIGNED]).
   */
  @Input() columns: MethodTypeColumn[] = [
    MethodTypeColumn.NAME,
    MethodTypeColumn.USED_BY_CASE_TYPES,
    MethodTypeColumn.USED_BY_SAMPLES,
    MethodTypeColumn.EDIT,
    MethodTypeColumn.DELETE,
  ];

  @Output() editClicked = new EventEmitter<MethodTypeListItem>();
  @Output() deleteClicked = new EventEmitter<MethodTypeListItem>();
  @Output() assignedToggled = new EventEmitter<MethodTypeListItem>();

  // Store the container height when loading starts to prevent layout jumps
  public containerHeight: number | null = null;

  constructor(private elementRef: ElementRef) {}

  get visibleColumns(): string[] {
    return this.columns.map((column) => column.valueOf());
  }

  ngOnChanges(changes: SimpleChanges): void {
    // When loading starts, capture the current height
    if (changes['loading'] && changes['loading'].currentValue === true) {
      const element = this.elementRef.nativeElement as HTMLElement;
      if (element && element.offsetHeight > 0) {
        this.containerHeight = element.offsetHeight;
      }
    }
    // When loading ends, clear the fixed height
    if (changes['loading'] && changes['loading'].currentValue === false) {
      this.containerHeight = null;
    }
  }
}

@NgModule({
  declarations: [MethodTypesListComponent],
  exports: [MethodTypesListComponent],
  imports: [
    CommonModule,
    TranslateModule,
    MatTableModule,
    MatSortModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    LoadingAttributeComponent,
    ListLoadingIndicatorComponent,
  ],
})
export class MethodTypesListComponentModule {}
